import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getRecognition, PrismArchive } from '../../api/assets';
import { formatTime } from '../../utils/time';
import { ApiError } from '../../api/request';
import ImageLightbox from '../../components/ImageLightbox';
import { viewUrl } from '../../utils/imgUrl';
import { AdoptBadge, EffectBadge, FeedbackCard, FeedbackItem, LinkedMaterial, RatingBadge } from './parts';
import { useProcessTypeText } from '../../utils/dictOptions';
import { useParamValueText } from '../../utils/paramValueText';

// 耗材识别详情页 (/assets/recognitions/:taskId): 实时代理 prism —
// 任务详情(候选+各工艺参数) + 用户反馈(含加工成品照) + 该任务后续的加工归档时间线.

interface Candidate {
    rank?: number;
    is_primary?: boolean;
    confidence?: number;
    is_official?: boolean;
    material_name?: string;
    material_category?: string;
    material_subtype?: string;
    surface_analysis?: string;
    parameter_source?: string;
    parameters?: Record<string, Record<string, unknown> | null>;
}

const PARAM_LABEL: Record<string, string> = {
    power: '功率', speed: '速度', repeat: '次数', dpi: 'DPI', density: '密度',
    bitmap_mode: '位图模式', laser_source: '光源', laser_power: '功率档',
    processing_mode: '加工模式', process_head: '加工头', frequency: '频率',
};

const HIDDEN_PARAM_KEYS = ['para_matrix', 'matrix_image', 'device_mode', 'z_axis_move',
    'z_layers', 'z_decline', 'min_power', 'dot_time'];


function RecognitionDetail() {
    // 工艺类型名从 dict process_type 取 (跨框架合并), 不在页面里维护常量表.
    const craftText = useProcessTypeText();
    // 候选参数里的枚举值 (加工模式/加工头/位图模式/光源) 走字典取名, 不裸显 code.
    const paramText = useParamValueText();
    const { id } = useParams<{ id: string }>();
    const [data, setData] = useState<Awaited<ReturnType<typeof getRecognition>> | null>(null);
    const [error, setError] = useState('');
    const [zoomSrc, setZoomSrc] = useState<string | null>(null);

    useEffect(() => {
        let alive = true;
        getRecognition(id)
            .then((r) => { if (alive) setData(r); })
            .catch((e) => { if (alive) setError(e instanceof ApiError ? e.message : String(e)); });
        return () => { alive = false; };
    }, [id]);

    const d = data?.detail;
    const candidates = useMemo(() => (d?.result?.candidates || []) as Candidate[], [d]);
    const feedbacks = useMemo(() => (data?.feedback || []) as FeedbackItem[], [data]);
    const procs = useMemo(() => (data?.processing?.data || []) as PrismArchive[], [data]);
    // 用户评价: 反馈里的 overall_rating (列表页的 rating 同源)
    const rating = feedbacks[0]?.overall_rating || '';

    if (error) return <div className="card p-4 text-sm text-rose-600 border-rose-200 bg-rose-50">错误: {error}</div>;
    if (!d) return <div className="text-center text-slate-400 text-sm py-16">加载中…</div>;

    const primary = candidates.find((c) => c.is_primary) || candidates[0];

    return (
        <div className="space-y-5">
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <div className="text-xs text-slate-400 mb-1">
                        <Link to="/assets/recognitions" className="text-brand-600 hover:underline">← 返回 材料识别</Link>
                    </div>
                    <div className="text-base font-semibold truncate">{primary?.material_name || d.task_id}</div>
                    <div className="text-xs text-slate-500 mt-1">
                        {primary?.material_category || '—'} · {d.device_model}{d.power ? ` ${d.power}W` : ''} ·
                        状态 {d.status} · 识别于 {formatTime(d.created_at)}
                        <span className="ml-2 font-mono text-slate-400">{d.task_id}</span>
                    </div>
                </div>
                {rating && <RatingBadge rating={rating} />}
            </div>

            <div className="grid grid-cols-[320px_1fr] gap-5 items-start">
                {/* 左: 识别图 + 反馈 */}
                <div className="space-y-3">
                    <div className="card p-3">
                        {d.image_url ? (
                            <img src={d.image_url} alt=""
                                className="w-full rounded-lg object-cover cursor-zoom-in border border-slate-100"
                                style={{ maxHeight: 280 }} onClick={() => setZoomSrc(d.image_url)} />
                        ) : (
                            <div className="w-full h-40 rounded-lg bg-slate-100" />
                        )}
                        {d.description && <div className="text-xs text-slate-500 mt-2">{d.description}</div>}
                    </div>
                    <div className="card p-3 text-xs text-slate-500 space-y-1.5">
                        <div className="text-xs font-semibold text-slate-500 mb-1">溯源</div>
                        <div className="flex items-start gap-2">
                            <span className="shrink-0">平台材料:</span>
                            <LinkedMaterial material={d.material} source={d.material_source} />
                        </div>
                        {d.material_clues && (
                            <div className="text-[11px] text-slate-400 leading-relaxed">
                                线索: 候选编码 <span className="font-mono">{d.material_clues.materialCode || '—'}</span>
                                {' · '}SKU <span className="font-mono">{d.material_clues.skuCode || '—'}</span>
                                {' · '}用户点选 <span className="font-mono">{d.material_clues.selectedMaterialId || '—'}</span>
                                {d.material_clues.selectedName ? `（${d.material_clues.selectedName}）` : ''}
                                {d.material_clues.isOfficial != null && ` · AI 判官方: ${d.material_clues.isOfficial ? '是' : '否'}`}
                            </div>
                        )}
                    </div>
                    <FeedbackCard items={feedbacks} onZoom={setZoomSrc} />
                </div>

                {/* 右: 候选 + 加工时间线 */}
                <div className="space-y-4">
                    <div className="card p-4 space-y-4">
                        <div className="text-xs font-semibold text-slate-500">
                            识别候选 ({candidates.length})
                            {d.result?.recognition_status && <span className="ml-2 font-normal text-slate-400">{d.result.recognition_status}</span>}
                        </div>
                        {candidates.length === 0 ? (
                            <div className="text-xs text-slate-400">无候选数据</div>
                        ) : (
                            candidates.map((cd, i) => (
                                <div key={cd.rank ?? i} className="border border-slate-100 rounded-xl p-3 space-y-2">
                                    <div className="flex items-center gap-2 text-[13px] flex-wrap">
                                        <span className="font-medium text-slate-700">#{cd.rank ?? i + 1} {cd.material_name || '—'}</span>
                                        {cd.is_primary && (
                                            <span className="px-1.5 py-0.5 rounded bg-brand-50 text-brand-600 text-[11px]">主候选</span>
                                        )}
                                        <span className="text-xs text-slate-400">
                                            {cd.material_subtype || cd.material_category || ''} ·
                                            置信度 {cd.confidence != null ? `${Math.round(cd.confidence * 100)}%` : '—'} ·
                                            {cd.is_official ? '官方' : '非官方'}
                                            {cd.parameter_source ? ` · 参数来源 ${cd.parameter_source}` : ''}
                                        </span>
                                    </div>
                                    {cd.surface_analysis && (
                                        <div className="text-xs text-slate-500 bg-slate-50 rounded-lg p-2">{cd.surface_analysis}</div>
                                    )}
                                    {cd.parameters && Object.values(cd.parameters).some((p) => p) && (
                                        <div className="grid grid-cols-2 gap-2">
                                            {Object.entries(cd.parameters).filter(([, params]) => params).map(([craft, params]) => (
                                                <div key={craft} className="rounded-lg bg-slate-50 p-2">
                                                    <div className="text-[11px] font-semibold text-slate-500 mb-1">
                                                        {craftText(craft)}
                                                    </div>
                                                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-600">
                                                        {Object.entries(params as Record<string, unknown>)
                                                            .filter(([, v]) => v !== null && v !== '' && v !== 0 && v !== false)
                                                            .filter(([pk]) => !HIDDEN_PARAM_KEYS.includes(pk))
                                                            .map(([pk, pv]) => (
                                                                <span key={pk}>{PARAM_LABEL[pk] || pk}=<b>{paramText(pk, pv)}</b></span>
                                                            ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>

                    <div className="card p-4">
                        <div className="text-xs font-semibold text-slate-500 mb-2">逐次加工明细 ({procs.length})</div>
                        {procs.length === 0 ? (
                            <div className="text-xs text-slate-400 leading-relaxed">
                                这次识别之后，还没有人拿它去加工。<br />
                                识别准不准，从这里看不出来 —— 要等有人真的用它加工过才有效果数据。
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {procs.map((p, i) => (
                                    <Link key={p.archive_id} to={`/assets/process-records/${p.archive_id}`}
                                        className="flex items-center gap-3 rounded-lg border border-slate-100 p-2 hover:border-brand-300">
                                        {p.image_url ? (
                                            <img src={p.image_url} alt="" loading="lazy"
                                                className="w-10 h-10 rounded object-cover border border-slate-100" />
                                        ) : (
                                            <div className="w-10 h-10 rounded bg-slate-100" />
                                        )}
                                        <div className="text-xs text-slate-600 min-w-0 flex-1">
                                            <div>第 {i + 1} 次 · {craftText(String(p.actual?.craft || ''))}</div>
                                            <div className="text-[11px] text-slate-400">{formatTime(p.archived_at)}</div>
                                        </div>
                                        <AdoptBadge verdict={p.verdict} wording="recognition" />
                                        <EffectBadge quality={p.effect?.quality || ''} source={p.effect?.source} wording="four" />
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <ImageLightbox src={viewUrl(zoomSrc)} onClose={() => setZoomSrc(null)} />
        </div>
    );
}

export default RecognitionDetail;
